<template>
  <div class="helpdesk">
    <div class="helpdesk__hero">
      <div class="helpdesk__hero-bg">
        <div class="helpdesk__hero-bg__triangle"></div>
      </div>
      <div class="helpdesk__hero-inner">
        <header class="helpdesk__topbar">
          <div class="helpdesk__brand">
            <a class="helpdesk__logo-link" :href="homeUrl" aria-label="Docendo">
              <span class="helpdesk__logo-back">&larr; {{ $t('vue2.helpdesk.back_to') }}</span>
              <do-logo class="helpdesk__logo"></do-logo>
            </a>
            <button type="button" class="helpdesk__brand-name" @click="goHome">Helpdesk</button>
          </div>
          <div class="helpdesk__topbar-right">
            <div class="helpdesk__langs" role="group" :aria-label="$t('vue2.helpdesk.language')">
              <button
                v-for="opt in localeOptions"
                :key="opt.code"
                type="button"
                class="helpdesk__lang"
                :class="{ 'helpdesk__lang--active': locale === opt.code }"
                :aria-label="opt.label"
                :aria-pressed="locale === opt.code ? 'true' : 'false'"
                :title="opt.label"
                @click="setLocale(opt.code)">
                <img class="helpdesk__lang-flag" :src="opt.flag" :alt="opt.label">
              </button>
            </div>
          </div>
        </header>

        <p class="helpdesk__eyebrow">{{ $t('vue2.helpdesk.hero_eyebrow') }}</p>
        <h1 class="helpdesk__title">{{ $t('vue2.helpdesk.hero_title') }}</h1>
        <p class="helpdesk__subtitle">{{ $t('vue2.helpdesk.hero_subtitle') }}</p>

        <div class="helpdesk__search">
          <span class="helpdesk__search-icon icon-search5"></span>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="helpdesk__search-input"
            :placeholder="$t('vue2.helpdesk.search_placeholder')"
            @input="onSearchInput"
            @keyup.esc="clearSearch">
          <button
            v-if="searchQuery"
            type="button"
            class="helpdesk__search-clear icon-cross2"
            :aria-label="$t('vue2.helpdesk.clear_search')"
            @click="clearSearch"></button>
        </div>
      </div>
    </div>

    <!-- Breadcrumb (fuld bredde) -->
    <div v-if="showSubheader" class="helpdesk__subheader">
      <div
        class="helpdesk__subheader-inner"
        :class="{ 'helpdesk__subheader-inner--wide': !!activeArticle }">
        <!-- Artikel-visning: link tilbage til kategorien -->
        <template v-if="activeArticle">
          <span class="helpdesk__crumb-label">{{ $t('vue2.helpdesk.articles_about') }}</span>
          <a
            v-if="articleCategory"
            class="helpdesk__crumb-link"
            @click="openCategory(articleCategory)">{{ articleCategory }}</a>
        </template>
        <!-- Kategori-visning -->
        <template v-else>
          <button type="button" class="helpdesk__crumb-back" @click="closeCategory">
            <span class="icon-arrow-left4"></span>
            {{ $t('vue2.helpdesk.back') }}
          </button>
          <span class="helpdesk__crumb-label">{{ $t('vue2.helpdesk.articles_about') }}</span>
          <span class="helpdesk__crumb-pill">{{ activeCategory }}</span>
          <span v-if="categoryDescription(activeCategory)" class="helpdesk__crumb-desc">
            {{ categoryDescription(activeCategory) }}
          </span>
        </template>
      </div>
    </div>

    <div class="helpdesk__body">
      <!-- Loading -->
      <div v-if="isLoading" class="helpdesk__container">
        <div class="helpdesk__loading">
          <div class="helpdesk__spinner"></div>
        </div>
      </div>

      <!-- Search results -->
      <div v-else-if="isSearching || searchQuery" class="helpdesk__container">
        <button type="button" class="helpdesk__back" @click="clearSearch">
          <span class="icon-arrow-left4"></span>
          {{ $t('vue2.helpdesk.back_to_start') }}
        </button>
        <h2 class="helpdesk__section-title">
          {{ $t('vue2.helpdesk.search_results') }}
          <span class="helpdesk__section-hint">"{{ searchQuery }}"</span>
        </h2>
        <div v-if="isSearching" class="helpdesk__loading helpdesk__loading--inline">
          <div class="helpdesk__spinner"></div>
        </div>
        <ul v-else-if="searchResults.length" class="helpdesk__article-list">
          <li
            v-for="article in searchResults"
            :key="article.id"
            class="helpdesk__article-item"
            @click="openArticle(article.id)">
            <span class="helpdesk__article-icon icon-file-text"></span>
            <span class="helpdesk__article-title">{{ article.title }}</span>
            <span class="helpdesk__article-arrow icon-arrow-right4"></span>
          </li>
        </ul>
        <p v-else class="helpdesk__empty">{{ $t('vue2.helpdesk.no_results') }}</p>
      </div>

      <!-- Single article: sidebar + content -->
      <div v-else-if="activeArticle" class="helpdesk__container helpdesk__container--article">
        <div class="helpdesk__layout">
          <aside class="helpdesk__sidebar">
            <button
              v-if="articleCategory"
              type="button"
              class="helpdesk__back-pill"
              @click="openCategory(articleCategory)">
              <span class="icon-arrow-left4"></span>
              <span class="helpdesk__back-pill-text">{{ articleCategory }}</span>
            </button>
            <template v-if="relatedArticles.length">
              <h3 class="helpdesk__sidebar-title">{{ $t('vue2.helpdesk.related_articles') }}</h3>
              <ul class="helpdesk__related-list">
                <li
                  v-for="article in relatedArticles"
                  :key="article.id"
                  class="helpdesk__related-item"
                  @click="openArticle(article.id)">
                  {{ article.title }}
                </li>
              </ul>
            </template>
          </aside>

          <article class="helpdesk__main helpdesk__article-card">
            <div class="helpdesk__article-top">
              <h1 class="helpdesk__article-heading">{{ activeArticle.title }}</h1>
              <button
                type="button"
                class="helpdesk__share"
                :class="{ 'helpdesk__share--done': shareCopied }"
                @click="shareArticle">
                <span :class="shareCopied ? 'icon-checkmark4' : 'icon-clipboard2'"></span>
                {{ shareCopied ? $t('vue2.helpdesk.link_copied') : $t('vue2.helpdesk.copy_link') }}
              </button>
            </div>
            <div class="helpdesk__article-body" v-html="articleHtml"></div>
          </article>
        </div>
      </div>

      <!-- Category detail: sidebar + article cards -->
      <div v-else-if="activeCategory" class="helpdesk__container helpdesk__container--wide">
        <div class="helpdesk__layout">
          <aside class="helpdesk__sidebar">
            <h3 class="helpdesk__sidebar-title">{{ $t('vue2.helpdesk.categories') }}</h3>
            <ul class="helpdesk__sidebar-list">
              <li
                v-for="cat in categories"
                :key="cat.name"
                class="helpdesk__sidebar-item"
                :class="{ 'helpdesk__sidebar-item--active': cat.name === activeCategory }"
                @click="openCategory(cat.name)">
                {{ cat.name }}
              </li>
            </ul>
          </aside>

          <div class="helpdesk__main">
            <div
              v-for="article in articlesInActiveCategory"
              :key="article.id"
              class="helpdesk__result-card"
              @click="openArticle(article.id)">
              <span class="helpdesk__result-icon icon-file-text"></span>
              <div class="helpdesk__result-content">
                <h4 class="helpdesk__result-title">{{ article.title }}</h4>
                <p v-if="articleExcerpt(article)" class="helpdesk__result-excerpt">
                  {{ articleExcerpt(article) }}
                </p>
              </div>
            </div>
            <p v-if="!articlesInActiveCategory.length" class="helpdesk__empty">
              {{ $t('vue2.helpdesk.no_articles') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Home -->
      <div v-else class="helpdesk__container helpdesk__home">
        <section v-if="featuredNews" class="helpdesk__news">
          <div class="helpdesk__news-main">
            <div class="helpdesk__news-top">
              <span class="helpdesk__news-eyebrow">
                {{ $t('vue2.helpdesk.latest_news') }}<template v-if="featuredNewsDate"> · {{ featuredNewsDate }}</template>
              </span>
            </div>
            <h2 class="helpdesk__news-title">{{ featuredNews.title }}</h2>
            <button type="button" class="helpdesk__news-button" @click="openArticle(featuredNews.id)">
              {{ $t('vue2.helpdesk.read_news') }}
              <span class="icon-arrow-right4"></span>
            </button>
          </div>
          <div v-if="olderNews.length" class="helpdesk__news-side">
            <h3 class="helpdesk__news-side-title">{{ $t('vue2.helpdesk.earlier_news') }}</h3>
            <ul class="helpdesk__news-list">
              <li
                v-for="article in olderNews"
                :key="article.id"
                class="helpdesk__news-item"
                @click="openArticle(article.id)">
                {{ article.title }}
              </li>
            </ul>
          </div>
        </section>

        <section v-if="mostVisited.length" class="helpdesk__section">
          <h2 class="helpdesk__section-title">{{ $t('vue2.helpdesk.popular_articles') }}</h2>
          <ul class="helpdesk__popular">
            <li
              v-for="article in mostVisited"
              :key="article.id"
              class="helpdesk__popular-item"
              @click="openArticle(article.id)">
              <span class="helpdesk__popular-icon icon-file-text"></span>
              <span class="helpdesk__popular-title">{{ article.title }}</span>
              <span class="helpdesk__popular-arrow icon-arrow-right4"></span>
            </li>
          </ul>
        </section>

        <section class="helpdesk__section">
          <div class="helpdesk__section-head">
            <h2 class="helpdesk__section-title">{{ $t('vue2.helpdesk.find_by_topic') }}</h2>
          </div>
          <div v-if="topicCategories.length" class="helpdesk__grid">
            <button
              v-for="(cat, index) in topicCategories"
              :key="cat.name"
              type="button"
              class="helpdesk__card"
              @click="openCategory(cat.name)">
              <span class="helpdesk__card-icon" :class="{ 'helpdesk__card-icon--image': !!cat.icon }">
                <img v-if="cat.icon" :src="cat.icon" alt="">
                <template v-else>{{ index + 1 }}</template>
              </span>
              <span class="helpdesk__card-content">
                <span class="helpdesk__card-title">{{ cat.name }}</span>
                <span v-if="cat.description" class="helpdesk__card-desc">{{ cat.description }}</span>
                <span class="helpdesk__card-count">
                  {{ cat.count }} {{ cat.count === 1 ? $t('vue2.helpdesk.article') : $t('vue2.helpdesk.articles') }} &rarr;
                </span>
              </span>
            </button>
          </div>
          <p v-else class="helpdesk__empty">{{ $t('vue2.helpdesk.no_articles') }}</p>
        </section>

        <p class="helpdesk__footer">{{ $t('vue2.helpdesk.footer_help') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import docendo from '../../docendo'
import DoLogo from '../common/DoLogo.vue'

// Sprogvalg til helpdesk-artikler (matcher Crisp/backend locale-koder).
var HELPDESK_LOCALES = [
  { code: 'da', flag: 'https://zen.docendo.co/flags/dk.svg', label: 'Dansk', dateLocale: 'da-DK' },
  { code: 'nb', flag: 'https://zen.docendo.co/flags/no.svg', label: 'Norsk', dateLocale: 'nb-NO' },
  { code: 'en', flag: 'https://zen.docendo.co/flags/us.svg', label: 'English', dateLocale: 'en-GB' }
]

var HELPDESK_LOCALE_CODES = HELPDESK_LOCALES.map(function (opt) { return opt.code })

function defaultHelpdeskLocale (vm) {
  var host = (typeof window !== 'undefined' && window.location && window.location.host) || ''
  if (host.indexOf('.dk') > -1) return 'da'
  if (host.indexOf('.no') > -1) return 'nb'
  if (vm && vm.$store && vm.$store.getters && vm.$store.getters.locale) {
    return vm.$store.getters.locale
  }
  if (vm && vm.$i18n && vm.$i18n.locale) return vm.$i18n.locale
  return 'en'
}

function normalizeHelpdeskLocale (code) {
  if (code === 'no' || code === 'nn') return 'nb'
  if (HELPDESK_LOCALE_CODES.indexOf(code) > -1) return code
  return null
}

export default {
  name: 'Helpdesk',
  components: { DoLogo },
  data () {
    return {
      rawCategories: [],
      mostVisited: [],
      latestNews: [],
      isLoading: false,
      searchQuery: '',
      searchResults: [],
      isSearching: false,
      activeCategory: null,
      activeArticle: null,
      searchDebounce: null,
      shareCopied: false,
      shareTimer: null,
      // Override for artikel-sprog (TLD er default; flag-skifter sætter denne).
      articleLocale: null,
      localeOptions: HELPDESK_LOCALES
    }
  },
  computed: {
    locale () {
      return this.articleLocale || defaultHelpdeskLocale(this)
    },
    homeUrl () {
      // Logoet peger altid på kalenderen på det app-domæne,
      // der matcher TLD'et, helpdesken besøges på.
      var host = (typeof window !== 'undefined' && window.location && window.location.host) || ''
      if (host.indexOf('.dk') > -1) return 'https://app.docendo.dk/calendar'
      if (host.indexOf('.no') > -1) return 'https://app.docendo.no/calendar'
      if (host.indexOf('.co') > -1) return 'https://app.docendo.co/calendar'
      return '/calendar'
    },
    // Kategorier i admin-rækkefølge; artikler uden kategori kommer sidst.
    categories () {
      var uncategorized = this.$t('vue2.helpdesk.uncategorized')
      return this.rawCategories
        .filter(cat => cat.articles && cat.articles.length)
        .map(cat => {
          var name = cat.name || uncategorized
          return {
            id: cat.id,
            name: name,
            description: cat.description || '',
            icon: cat.icon || null,
            count: cat.articles.length,
            articles: cat.articles.map(article => Object.assign({}, article, { category: name }))
          }
        })
    },
    articles () {
      return this.categories.reduce((all, cat) => all.concat(cat.articles), [])
    },
    newsCategoryName () {
      var first = this.latestNews[0]
      return first ? first.category : null
    },
    // Nyhedskategorien har sin egen plads (fremhævet kort + "Nyheder"-fanen).
    topicCategories () {
      return this.categories.filter(cat => cat.name !== this.newsCategoryName)
    },
    featuredNews () {
      return this.latestNews[0] || null
    },
    olderNews () {
      return this.latestNews.slice(1, 4)
    },
    featuredNewsDate () {
      if (!this.featuredNews || !this.featuredNews.published_at) return ''
      var opt = HELPDESK_LOCALES.find(o => o.code === this.locale)
      try {
        return new Date(this.featuredNews.published_at)
          .toLocaleDateString(opt ? opt.dateLocale : undefined, { month: 'long', year: 'numeric' })
      } catch (e) {
        return ''
      }
    },
    articlesInActiveCategory () {
      if (!this.activeCategory) return []
      return this.articles.filter(article => article.category === this.activeCategory)
    },
    articleCategory () {
      if (this.activeArticle && this.activeArticle.category) {
        return this.activeArticle.category
      }
      return this.activeCategory
    },
    relatedArticles () {
      if (!this.articleCategory) return []
      var currentId = this.activeArticle ? this.activeArticle.id : null
      return this.articles.filter(article => {
        return article.category === this.articleCategory && article.id !== currentId
      })
    },
    showSubheader () {
      return !this.isLoading &&
        !this.searchQuery &&
        !this.isSearching &&
        (!!this.activeArticle || !!this.activeCategory)
    },
    articleHtml () {
      if (!this.activeArticle) return ''
      var html = this.activeArticle.as_html || this.activeArticle.body || ''
      return this.processArticleHtml(html)
    }
  },
  created () {
    this._previousUiLocale = this.$i18n ? this.$i18n.locale : null
    var queryLang = this.$route && this.$route.query
      ? normalizeHelpdeskLocale(this.$route.query.lang)
      : null
    this.articleLocale = queryLang || defaultHelpdeskLocale(this)
    this.syncUiLocale(this.articleLocale)
  },
  beforeMount () {
    this.$store.dispatch('getUser')
  },
  beforeDestroy () {
    if (this.$i18n && this._previousUiLocale) {
      this.$i18n.locale = this._previousUiLocale
    }
  },
  mounted () {
    this.fetchArticles()
    // Gendan visningen fra URL'en (fx hvis man lander direkte på ?article=14).
    if (this.$route) {
      this.applyState(this.$route.query || {})
    }
  },
  watch: {
    // Reagér på browserens frem/tilbage (og øvrige query-ændringer).
    '$route.query': function (query) {
      this.applyState(query || {})
    }
  },
  methods: {
    syncUiLocale (code) {
      if (this.$i18n && code) {
        this.$i18n.locale = code
      }
    },
    setLocale (code) {
      var next = normalizeHelpdeskLocale(code)
      if (!next || next === this.locale) return
      this.articleLocale = next
      this.syncUiLocale(next)
      // Artikel-id'er er locale-specifikke — gå tilbage til forsiden.
      this.activeArticle = null
      this.activeCategory = null
      this.searchQuery = ''
      this.searchResults = []
      this.isSearching = false
      this.fetchArticles()
      this.navigate({}, true)
    },
    fetchArticles () {
      this.isLoading = true
      docendo.articlesIndexNew(this.locale, data => {
        data = data || {}
        this.rawCategories = Array.isArray(data.categories) ? data.categories : []
        this.mostVisited = Array.isArray(data.most_visited) ? data.most_visited : []
        this.latestNews = Array.isArray(data.latest_news) ? data.latest_news : []
        this.isLoading = false
      }, () => {
        this.rawCategories = []
        this.mostVisited = []
        this.latestNews = []
        this.isLoading = false
      })
    },
    categoryDescription (name) {
      var cat = this.categories.find(c => c.name === name)
      return cat ? cat.description : ''
    },
    processArticleHtml (html) {
      if (!html) return ''
      if (typeof window === 'undefined' || !window.DOMParser) return html

      var doc = new window.DOMParser().parseFromString(html, 'text/html')
      var each = function (nodeList, fn) {
        Array.prototype.forEach.call(nodeList, fn)
      }

      // Fjern Crisp' tomme "legacy-anchor"-spans.
      each(doc.querySelectorAll('.legacy-anchor'), function (el) {
        el.parentNode && el.parentNode.removeChild(el)
      })

      // Billeder må aldrig være bredere end indholdet.
      each(doc.querySelectorAll('img'), function (img) {
        img.removeAttribute('width')
        img.removeAttribute('height')
        img.style.maxWidth = '100%'
        img.style.height = 'auto'
      })

      // ${vimeo}<a href="ID"></a> → responsiv Vimeo-embed.
      each(doc.querySelectorAll('a'), function (a) {
        var href = a.getAttribute('href') || ''
        if (!/^\d+$/.test(href)) return
        var wrap = doc.createElement('div')
        wrap.className = 'helpdesk__video'
        var iframe = doc.createElement('iframe')
        iframe.setAttribute('src', 'https://player.vimeo.com/video/' + href)
        iframe.setAttribute('frameborder', '0')
        iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture')
        iframe.setAttribute('allowfullscreen', 'true')
        wrap.appendChild(iframe)
        var target = a.closest ? (a.closest('p') || a) : a
        target.parentNode && target.parentNode.replaceChild(wrap, target)
      })

      // Gør overskrifternes selv-links til ren tekst (fjern "#"-ankre).
      each(doc.querySelectorAll('a[href^="#"]'), function (a) {
        var text = doc.createTextNode(a.textContent || '')
        a.parentNode && a.parentNode.replaceChild(text, a)
      })

      // Eksterne links åbner i nyt faneblad.
      each(doc.querySelectorAll('a[href^="http"]'), function (a) {
        a.setAttribute('target', '_blank')
        a.setAttribute('rel', 'noopener noreferrer')
      })

      var result = doc.body ? doc.body.innerHTML : html
      // Ryd eventuelle resterende ${vimeo}-tokens.
      return result.replace(/\$\{vimeo\}/g, '')
    },
    articleExcerpt (article) {
      var raw = article && (article.excerpt || article.body)
      if (!raw) return ''
      var text = String(raw)
        .replace(/<[^>]*>/g, ' ') // html-tags
        .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ') // markdown-billeder
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // markdown-links → tekst
        .replace(/\$\{vimeo\}/g, ' ') // vimeo-token
        .replace(/[#*_`>]/g, ' ') // markdown-tegn
        .replace(/\s+/g, ' ')
        .trim()
      if (text.length > 160) {
        text = text.slice(0, 160).trim() + '…'
      }
      return text
    },
    // Naviger via router-query, så browserens frem/tilbage virker.
    // Falder tilbage til intern state, hvis komponenten kører uden router
    // (f.eks. som global <helpdesk> på en Rails-side).
    navigate (query, replace) {
      var clean = { lang: this.locale }
      Object.keys(query || {}).forEach(key => {
        var value = query[key]
        if (value !== null && value !== undefined && value !== '') {
          clean[key] = String(value)
        }
      })
      if (this.$router && this.$route) {
        if (JSON.stringify(this.$route.query) === JSON.stringify(clean)) {
          return
        }
        var method = replace ? 'replace' : 'push'
        this.$router[method]({ query: clean }).catch(() => {})
      } else {
        this.applyState(clean)
      }
    },
    applyState (query) {
      query = query || {}

      var lang = normalizeHelpdeskLocale(query.lang)
      if (lang && lang !== this.articleLocale) {
        this.articleLocale = lang
        this.syncUiLocale(lang)
        this.activeArticle = null
        this.fetchArticles()
      }

      this.searchQuery = query.q || ''
      this.activeCategory = query.category || null

      var articleId = query.article || null
      if (articleId) {
        if (!this.activeArticle || String(this.activeArticle.id) !== String(articleId)) {
          this.fetchArticle(articleId)
        }
      } else {
        this.activeArticle = null
      }

      if (this.searchQuery) {
        this.isSearching = true
        this.runSearch(this.searchQuery)
      } else {
        this.searchResults = []
        this.isSearching = false
      }
      this.scrollToTop()
    },
    fetchArticle (id) {
      this.isLoading = true
      docendo.articlesGet(id, this.locale, data => {
        this.activeArticle = data && !data.status ? data : null
        this.isLoading = false
        this.scrollToTop()
        if (this.activeArticle && this.activeArticle.title) {
          this.$store.dispatch('trackEvent', 'helpdesk_article_' + this.activeArticle.title)
        }
      }, () => {
        this.isLoading = false
      })
    },
    goHome () {
      this.navigate({})
    },
    openCategory (name) {
      this.navigate({ category: name })
    },
    closeCategory () {
      this.navigate({})
    },
    openArticle (id) {
      this.navigate({ article: id })
    },
    closeArticle () {
      this.navigate({})
    },
    onSearchInput () {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce)
      }
      var query = this.searchQuery
      if (!query || !query.trim()) {
        this.navigate({}, true)
        return
      }
      this.isSearching = true
      this.searchDebounce = setTimeout(() => {
        this.navigate({ q: query }, true)
      }, 350)
    },
    runSearch (query) {
      docendo.articlesSearch(query, this.locale, data => {
        if (this.searchQuery !== query) return
        this.searchResults = Array.isArray(data) ? data : []
        this.isSearching = false
      }, () => {
        this.searchResults = []
        this.isSearching = false
      })
    },
    clearSearch () {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce)
      }
      this.navigate({})
    },
    scrollToTop () {
      if (typeof window !== 'undefined' && window.scrollTo) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    articleShareUrl () {
      if (typeof window === 'undefined' || !this.activeArticle) return ''
      // Når en artikel vises, er den aktuelle URL allerede det delbare link
      // (virker både i hash-mode, fx .../app#/helpdesk?article=75, og history-mode).
      if (this.$router && this.$route) {
        var resolved = this.$router.resolve({
          query: Object.assign({}, this.$route.query, { article: String(this.activeArticle.id) })
        })
        var href = resolved.href || ''
        if (href.charAt(0) === '#') {
          return window.location.origin + window.location.pathname + href
        }
        if (href.charAt(0) === '/') {
          return window.location.origin + href
        }
      }
      return window.location.href
    },
    shareArticle () {
      var url = this.articleShareUrl()
      if (!url) return
      this.copyToClipboard(url)
    },
    copyToClipboard (text) {
      var self = this
      var done = function () {
        self.shareCopied = true
        if (self.shareTimer) {
          clearTimeout(self.shareTimer)
        }
        self.shareTimer = setTimeout(function () {
          self.shareCopied = false
        }, 2000)
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(function () {
          self.fallbackCopy(text, done)
        })
      } else {
        this.fallbackCopy(text, done)
      }
    },
    fallbackCopy (text, done) {
      try {
        var el = document.createElement('textarea')
        el.value = text
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        done()
      } catch (e) {}
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.helpdesk {
  --hd-blue: #4553ff;
  --hd-blue-soft: #eef0ff;
  --hd-blue-border: #d6dbff;

  min-height: 100vh;
  background: var(--bg-grey, #edf0f5);
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #2c3e50;
}

.helpdesk__hero {
  position: relative;
  padding: 0 1.5rem 3.5rem;
  overflow: hidden;
  background: #4553ff;
  background: linear-gradient(90deg, #4553ff, #7f7fd5);
}

.helpdesk__hero-bg {
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  transform: skewY(-6deg);
  transform-origin: 0;
  background: #4553ff;
  background: linear-gradient(90deg, #4553ff, #7f7fd5);
}

.helpdesk__hero-bg__triangle {
  width: 100%;
  height: 90px;
  background: hsla(0, 0%, 100%, 0.1);
}

.helpdesk__hero-inner {
  position: relative;
  z-index: 1;
  max-width: 900px;
  margin: 0 auto;
}

.helpdesk__topbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 0 2.5rem;
}

.helpdesk__brand {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  color: #fff;
}

.helpdesk__logo-link {
  display: flex;
  align-items: flex-end;
  text-decoration: none;
  overflow: hidden;
}

.helpdesk__logo-back {
  display: inline-block;
  max-width: 0;
  overflow: hidden;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  font-weight: 500;
  opacity: 0;
  transition: max-width 0.3s ease, opacity 0.25s ease, margin 0.3s ease;
  margin-right: 0;
}

.helpdesk__logo-link:hover .helpdesk__logo-back {
  max-width: 6em;
  opacity: 1;
  margin-right: 0.4rem;
}

.helpdesk__logo {
  width: 120px;
  height: auto;
  display: block;
  transition: transform 0.3s ease;
}

.helpdesk__logo-link:hover .helpdesk__logo {
  transform: translateX(2px);
}

.helpdesk__brand-name {
  border: none;
  background: none;
  height: auto;
  padding: 0;
  margin-left: 0.25rem;
  /* Cancels the descender space below the baseline so the text sits on the logo's bottom edge */
  margin-bottom: -0.13em;
  color: #fff;
  font-family: inherit;
  font-size: 1.1rem;
  font-weight: 500 !important;
  line-height: 1;
  opacity: 0.85;
  cursor: pointer;
}

.helpdesk__brand-name:hover {
  opacity: 1;
}

.helpdesk__topbar-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.helpdesk__langs {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.helpdesk__lang {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem;
  margin: 0;
  border: 2px solid transparent;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  opacity: 0.65;
  transition: opacity 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.helpdesk__lang:hover,
.helpdesk__lang:focus {
  opacity: 1;
  outline: none;
  background: rgba(255, 255, 255, 0.12);
}

.helpdesk__lang--active {
  opacity: 1;
  border-color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.15);
}

.helpdesk__lang-flag {
  display: block;
  width: 22px;
  height: 15px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
}

.helpdesk__eyebrow {
  text-align: center;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin: 0 0 0.6rem;
}

.helpdesk__title {
  text-align: center;
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.4rem;
}

.helpdesk__subtitle {
  text-align: center;
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  margin: 0 0 1.75rem;
}

.helpdesk__search {
  position: relative;
  max-width: 640px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 30px -8px rgba(24, 40, 180, 0.45);
  padding: 0 1.25rem;
}

.helpdesk__search-icon {
  color: #8794a5;
  font-size: 1rem;
  flex-shrink: 0;
}

.helpdesk__search-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 1rem 0.85rem;
  font-size: 1rem;
  color: #2c3e50;
  background: transparent;
  font-family: inherit;
}

.helpdesk__search-input::placeholder {
  color: #a7b0bd;
}

.helpdesk__search-clear {
  border: none;
  background: transparent;
  color: #8794a5;
  cursor: pointer;
  font-size: 0.8rem;
  flex-shrink: 0;
  padding: 0.25rem;
}

.helpdesk__body {
  padding: 0 1.5rem 4rem;
}

.helpdesk__container {
  max-width: 900px;
  margin: 0 auto;
}

.helpdesk__container--wide {
  max-width: 1040px;
}

.helpdesk__container--article {
  max-width: 1400px;
}

.helpdesk__subheader-inner--wide {
  max-width: 1400px;
}

/* Forside */
.helpdesk__home {
  padding-top: 2rem;
}

.helpdesk__news {
  display: flex;
  gap: 2rem;
  padding: 1.75rem 2rem;
  margin-bottom: 2.5rem;
  background: var(--hd-blue-soft);
  border: 1px solid var(--hd-blue-border);
  border-radius: 12px;
}

.helpdesk__news-main {
  flex: 1;
  min-width: 0;
}

.helpdesk__news-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.helpdesk__news-eyebrow {
  color: var(--hd-blue);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.helpdesk__news-title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  color: #2c3e50;
  margin: 0 0 1.25rem;
}

.helpdesk__news-button {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1.4rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(90deg, #4553ff, #7f7fd5);
  color: #fff;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 18px -8px rgba(69, 83, 255, 0.7);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.helpdesk__news-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px -8px rgba(69, 83, 255, 0.8);
}

.helpdesk__news-side {
  width: 260px;
  flex-shrink: 0;
  padding-left: 2rem;
  border-left: 1px solid var(--hd-blue-border);
}

.helpdesk__news-side-title {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8794a5;
  margin: 0 0 0.5rem;
}

.helpdesk__news-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.helpdesk__news-item {
  position: relative;
  padding: 0.45rem 0 0.45rem 0.9rem;
  font-size: 0.9rem;
  line-height: 1.4;
  color: #5c6b7a;
  cursor: pointer;
}

.helpdesk__news-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.95em;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c2cad4;
}

.helpdesk__news-item:hover {
  color: var(--hd-blue);
}

.helpdesk__section {
  margin-bottom: 2.5rem;
}

.helpdesk__section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
}

.helpdesk__popular {
  list-style: none;
  margin: 0;
  padding: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.helpdesk__popular-item {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.95rem 1.25rem;
  cursor: pointer;
  border-bottom: 1px solid #eef0f4;
  transition: background 0.12s;
}

.helpdesk__popular-item:last-child {
  border-bottom: none;
}

.helpdesk__popular-item:hover {
  background: #f6f7ff;
}

.helpdesk__popular-icon {
  color: var(--hd-blue);
  font-size: 1rem;
  flex-shrink: 0;
}

.helpdesk__popular-title {
  flex: 1;
  min-width: 0;
  font-size: 0.95rem;
  color: #2c3e50;
}

.helpdesk__popular-arrow {
  color: #c2cad4;
  font-size: 0.85rem;
  flex-shrink: 0;
  transition: color 0.12s, transform 0.12s;
}

.helpdesk__popular-item:hover .helpdesk__popular-arrow {
  color: var(--hd-blue);
  transform: translateX(2px);
}

.helpdesk__footer {
  text-align: center;
  color: #8794a5;
  font-size: 0.9rem;
  margin: 1rem 0 0;
}

/* Kategori-breadcrumb */
.helpdesk__subheader {
  background: #fff;
  border-bottom: 1px solid #e9edf2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.helpdesk__subheader-inner {
  max-width: 1040px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem 1.5rem;
  flex-wrap: wrap;
}

.helpdesk__crumb-back {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  color: #2c3e50;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
}

.helpdesk__crumb-back:hover {
  color: var(--hd-blue);
}

.helpdesk__crumb-label {
  color: #8794a5;
  font-size: 0.9rem;
  margin-left: 0.5rem;
}

.helpdesk__crumb-pill {
  background: var(--hd-blue);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  white-space: nowrap;
}

.helpdesk__crumb-desc {
  color: #8794a5;
  font-size: 0.9rem;
  flex: 1;
  min-width: 200px;
}

.helpdesk__crumb-link {
  color: var(--hd-blue);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}

.helpdesk__crumb-link:hover {
  text-decoration: underline;
}

/* Kategori-layout: sidebar + kort */
.helpdesk__layout {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  padding-top: 2.5rem;
}

.helpdesk__sidebar {
  width: 220px;
  flex-shrink: 0;
}

.helpdesk__sidebar-title {
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 0.75rem;
  padding-left: 0.85rem;
}

.helpdesk__sidebar-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.helpdesk__sidebar-item {
  padding: 0.55rem 0.85rem;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #5c6b7a;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background 0.12s, color 0.12s;
}

.helpdesk__sidebar-item:hover {
  background: #e4e7f5;
  color: #2c3e50;
}

.helpdesk__sidebar-item--active,
.helpdesk__sidebar-item--active:hover {
  background: var(--hd-blue-soft);
  color: var(--hd-blue);
  font-weight: 600;
}

/* Artikel-sidebar: tilbage-pille + relaterede artikler */
.helpdesk__back-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 100%;
  border: none;
  border-radius: 6px;
  background: var(--hd-blue);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 0.4rem 0.7rem;
  cursor: pointer;
  font-family: inherit;
  margin-bottom: 1.75rem;
}

.helpdesk__back-pill:hover {
  filter: brightness(0.95);
}

.helpdesk__back-pill-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.helpdesk__related-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.helpdesk__related-item {
  font-size: 0.9rem;
  color: #5c6b7a;
  line-height: 1.4;
  padding: 0.55rem 0;
  cursor: pointer;
  border-bottom: 1px solid #e4e7ee;
}

.helpdesk__related-item:last-child {
  border-bottom: none;
}

.helpdesk__related-item:hover {
  color: var(--hd-blue);
}

.helpdesk__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.helpdesk__result-card {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  background: #fff;
  border-radius: 10px;
  padding: 1.25rem 1.5rem;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.helpdesk__result-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px -8px rgba(50, 50, 93, 0.25);
}

.helpdesk__result-icon {
  color: var(--hd-blue);
  font-size: 1.1rem;
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.helpdesk__result-content {
  flex: 1;
  min-width: 0;
}

.helpdesk__result-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  line-height: 1.35;
}

.helpdesk__result-excerpt {
  font-size: 0.9rem;
  color: #8794a5;
  line-height: 1.5;
  margin: 0.35rem 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.helpdesk__section-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 1rem;
  color: #2c3e50;
}

.helpdesk__section-hint {
  color: #8794a5;
  font-weight: 500;
  margin-left: 0.25rem;
}

.helpdesk__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.helpdesk__card {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  width: 100%;
  height: 100%;
  background: #fff;
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 1.15rem 1.25rem;
  cursor: pointer;
  text-align: left;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
}

.helpdesk__card:hover {
  transform: translateY(-2px);
  border-color: var(--hd-blue-border);
  box-shadow: 0 8px 20px -6px rgba(50, 50, 93, 0.25);
}

.helpdesk__card-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--hd-blue-soft);
  color: var(--hd-blue);
  font-size: 0.9rem;
  font-weight: 700;
  flex-shrink: 0;
  overflow: hidden;
}

.helpdesk__card-icon--image {
  width: 40px;
  height: 40px;
  background: transparent;
}

.helpdesk__card-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.helpdesk__card-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  min-width: 0;
}

.helpdesk__card-title {
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
}

.helpdesk__card-desc {
  color: #5c6b7a;
  font-size: 0.85rem;
  line-height: 1.45;
}

.helpdesk__card-count {
  margin-top: 0.2rem;
  font-size: 0.8rem;
  color: #8794a5;
}

.helpdesk__card:hover .helpdesk__card-count {
  color: var(--hd-blue);
}

.helpdesk__back {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  color: var(--hd-blue);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin: 1.5rem 0 1rem;
  font-family: inherit;
}

.helpdesk__back:hover {
  text-decoration: underline;
}

.helpdesk__article-list {
  list-style: none;
  margin: 0;
  padding: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.helpdesk__article-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  cursor: pointer;
  border-bottom: 1px solid #eef0f4;
  transition: background 0.12s;
}

.helpdesk__article-item:last-child {
  border-bottom: none;
}

.helpdesk__article-item:hover {
  background: #f6f7ff;
}

.helpdesk__article-icon {
  color: var(--hd-blue);
  font-size: 1rem;
  flex-shrink: 0;
}

.helpdesk__article-title {
  flex: 1;
  font-size: 0.95rem;
  color: #2c3e50;
  min-width: 0;
}

.helpdesk__article-arrow {
  color: #c2cad4;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.helpdesk__article-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 2.5rem;
}

.helpdesk__article-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.helpdesk__article-heading {
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  color: var(--hd-blue);
}

.helpdesk__share {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  border: 1px solid #dfe3ea;
  background: #fff;
  color: #5c6b7a;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.45rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.helpdesk__share:hover {
  background: #f6f7ff;
  color: var(--hd-blue);
  border-color: var(--hd-blue-border);
}

.helpdesk__share--done {
  background: #e9faf2;
  color: var(--green, #31cd87);
  border-color: #b9edd4;
}

.helpdesk__article-body {
  font-size: 1rem;
  line-height: 1.65;
  color: #4a5568;
}

.helpdesk__loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5rem 0;
}

.helpdesk__loading--inline {
  padding: 2.5rem 0;
}

.helpdesk__spinner {
  width: 34px;
  height: 34px;
  border: 3px solid #e0e2ea;
  border-top-color: var(--hd-blue);
  border-radius: 50%;
  animation: helpdesk-spin 0.7s linear infinite;
}

@keyframes helpdesk-spin {
  to { transform: rotate(360deg); }
}

.helpdesk__empty {
  text-align: center;
  color: #8794a5;
  padding: 3rem 0;
  font-size: 1rem;
}

@media (max-width: 768px) {
  .helpdesk__topbar {
    flex-wrap: wrap;
    align-items: center;
  }

  .helpdesk__grid {
    grid-template-columns: 1fr;
  }

  .helpdesk__news {
    flex-direction: column;
    gap: 1.25rem;
    padding: 1.5rem;
  }

  .helpdesk__news-side {
    width: auto;
    padding-left: 0;
    padding-top: 1rem;
    border-left: none;
    border-top: 1px solid var(--hd-blue-border);
  }

  .helpdesk__article-card {
    padding: 1.5rem;
  }

  .helpdesk__title {
    font-size: 1.5rem;
  }

  .helpdesk__layout {
    flex-direction: column;
    padding-top: 1.5rem;
  }

  .helpdesk__sidebar {
    width: 100%;
  }

  .helpdesk__sidebar-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  .helpdesk__sidebar-item {
    background: #fff;
  }

  .helpdesk__crumb-desc {
    flex-basis: 100%;
  }
}
</style>

<!--
  Bevidst IKKE scoped: indholdet i .helpdesk__article-body indsættes via v-html
  og får derfor ikke Vue's scope-attribut. Alle selektorer er præfikset med den
  unikke klasse .helpdesk__article-body, så de kun rammer helpdesk-artiklen og
  ikke resten af projektet.

  html-fonten i appen er 12px (base.css / Rails base.scss), så 1rem = 12px.
  rem kan ikke scopes til et subtree, derfor sættes root-fonten til 14px kun
  mens .helpdesk er i DOM'en. Resten af appen er uændret.
-->
<style>
html:has(.helpdesk) {
  font-size: 14px;
}
.helpdesk__article-body h1,
.helpdesk__article-body h2,
.helpdesk__article-body h3 {
  color: #2c3e50;
  margin: 2rem 0 0.75rem;
  line-height: 1.3;
  font-weight: 700;
}

.helpdesk__article-body h1 {
  font-size: 1.45rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eef0f4;
}

.helpdesk__article-body h2 {
  font-size: 1.2rem;
}

.helpdesk__article-body h3 {
  font-size: 1.05rem;
}

.helpdesk__article-body p {
  margin: 0 0 1rem;
}

.helpdesk__article-body .helpdesk__video {
  position: relative;
  padding-top: 56.25%;
  height: 0;
  margin: 1.25rem 0;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.helpdesk__article-body .helpdesk__video iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.helpdesk__article-body a {
  color: #4553ff;
  text-decoration: underline;
}

.helpdesk__article-body ul,
.helpdesk__article-body ol {
  margin: 0 0 1rem;
  padding-left: 1.5rem;
}

.helpdesk__article-body ol {
  list-style: decimal;
}

.helpdesk__article-body li {
  margin-bottom: 0.4rem;
}

/* Punktlister får et farvet bullet-pseudoelement (den globale
   list-style-type: none fjerner ellers markeringen). */
.helpdesk__article-body ul {
  list-style: none;
  padding-left: 0;
}

.helpdesk__article-body ul > li {
  position: relative;
  padding-left: 1.4rem;
}

.helpdesk__article-body ul > li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.62em;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #4553ff;
}

.helpdesk__article-body table {
  width: 100%;
  max-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #e5e8ee;
  border-right: none;
  border-radius: 8px;
  overflow: hidden;
  font-size: 0.9rem;
  background: #fff;
  margin: 1.25rem 0;
  table-layout: auto;
}

.helpdesk__article-body caption {
  caption-side: top;
  text-align: left;
  font-weight: 700;
  color: #8a7415;
  background: #fdf6cf;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #efe4a8;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
}

.helpdesk__article-body th,
.helpdesk__article-body td {
  padding: 0.6rem 1rem;
  text-align: left;
  vertical-align: top;
  border-bottom: 1px solid #eef0f4;
  border-right: 1px solid #eef0f4;
}

.helpdesk__article-body thead th {
  color: #8794a5;
  font-weight: 600;
  font-size: 0.78rem;
  text-transform: none;
  background: #f7f8fa;
  white-space: nowrap;
}

.helpdesk__article-body tbody td {
  color: #4a5568;
}

.helpdesk__article-body tbody tr:last-child td,
.helpdesk__article-body tbody tr:last-child th {
  border-bottom: none;
}

.helpdesk__article-body tbody tr:hover td {
  background: #f9fafc;
}

.helpdesk__article-body img {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
  border: 1px solid #eef0f4;
  display: block;
  margin: 1rem 0;
}

.helpdesk__article-body pre {
  background: #f4f5f7;
  padding: 1rem;
  border-radius: 6px;
  overflow-x: auto;
}

.helpdesk__article-body blockquote {
  border-left: 3px solid #4553ff;
  margin: 1rem 0;
  padding: 0.25rem 0 0.25rem 1rem;
  color: #606c76;
}
</style>



// WEBPACK FOOTER //
// Helpdesk.vue?3fa6bd01